import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { customerAPI, loadAPI, documentAPI, interactionAPI, laneAPI, userAPI, facilityAPI } from '../api';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { ArrowLeft, Phone, Mail, Plus, FileText, MessageSquare, MapPin, Trash2, Users, Fuel } from 'lucide-react';
import { CustomerFuelSurchargeTab } from '../components/CustomerFuelSurchargeTab';
import { UpdatesDialog } from '../components/load-workspace/UpdatesDialog';
import { LoadsSpreadsheet, useListColumns } from '../components/loads/LoadsSpreadsheet';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [customer, setCustomer] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    contact_name: '', phone: '', email: '',
    account_status: 'Active', credit_limit: 0, credit_usage: 0,
    credit_terms: 'Net 30', primary_contact: '', secondary_contact: '',
    account_manager_id: '', account_manager_name: '', account_manager_commission_pct: 0,
    operators: []
  });
  const [loads, setLoads] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const listColumns = useListColumns(false);
  const [documents, setDocuments] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [lanes, setLanes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState('profile');
  const [newLane, setNewLane] = useState({ origin: '', destination: '', stops: 0, line_haul: 0, accessorials: 0 });


  const fetchUsers = useCallback(async () => {
    try {
      const usersRes = await userAPI.getAll();
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  }, []);

  const fetchCustomerData = useCallback(async () => {
    try {
      const [customerRes, allLoads, docs, inters, lanesRes, facRes] = await Promise.all([
        customerAPI.getById(id),
        loadAPI.getAll(),
        documentAPI.getByEntity('customer', id),
        interactionAPI.getByEntity('customer', id),
        laneAPI.getByCustomer(id),
        facilityAPI.getAll(),
      ]);
      setCustomer(customerRes.data);
      setLoads(allLoads.data.filter(l => l.customer_id === id));
      setFacilities(facRes.data || []);
      setDocuments(docs.data);
      setInteractions(inters.data);
      setLanes(lanesRes.data);
    } catch (error) {
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUsers();
    if (!isNew) fetchCustomerData();
  }, [id, isNew, fetchUsers, fetchCustomerData]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (isNew) {
        await customerAPI.create(customer);
        toast.success('Customer created successfully');
      } else {
        await customerAPI.update(id, customer);
        toast.success('Customer updated successfully');
      }
      navigate('/customers');
    } catch (error) {
      toastApiError(toast, error, 'Customer could not be saved');
    }
  };

  const handleAddNote = async () => {
    const content = prompt('Enter note:');
    if (content) {
      try {
        await interactionAPI.create({
          entity_type: 'customer',
          entity_id: id,
          interaction_type: 'note',
          subject: 'Customer Note',
          content,
          attachments: []
        });
        toast.success('Note added');
        fetchCustomerData();
      } catch (error) {
        toast.error('Failed to add note');
      }
    }
  };

  const handleAddLane = async () => {
    if (!newLane.origin || !newLane.destination) {
      toast.error('Origin and destination are required');
      return;
    }
    try {
      await laneAPI.create({
        customer_id: id,
        ...newLane
      });
      toast.success('Lane added successfully');
      setNewLane({ origin: '', destination: '', stops: 0, line_haul: 0, accessorials: 0 });
      fetchCustomerData();
    } catch (error) {
      toast.error('Failed to add lane');
    }
  };

  const handleDeleteLane = async (laneId) => {
    if (!window.confirm('Delete this lane?')) return;
    try {
      await laneAPI.delete(laneId);
      toast.success('Lane deleted');
      fetchCustomerData();
    } catch (error) {
      toast.error('Failed to delete lane');
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-full"><div className="text-[var(--tms-text-secondary)]">Loading...</div></div></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="p-8" data-testid="customer-detail-page">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/customers')} className="p-2 hover:bg-[var(--tms-hover)] rounded" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-[var(--tms-text)]">
                {isNew ? 'New Customer' : customer.name}
              </h1>
              {!isNew && <p className="text-[var(--tms-text-secondary)] mt-1">Customer ID: {id.substring(0, 8)}</p>}
            </div>
          </div>
          {!isNew && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setUpdatesOpen(true)} data-testid="updates-customer-button">
                Updates
              </Button>
              <Button
                variant="outline"
                className="text-[var(--tms-error)] border-[var(--tms-error)] hover:bg-[var(--tms-error)]/10"
                onClick={async () => {
                  const reason = window.prompt('Why are you deleting this customer? (required)');
                  if (reason === null) return;
                  if (!reason.trim()) { toast.error('A reason is required to delete'); return; }
                  try {
                    await customerAPI.delete(id, reason.trim());
                    toast.success('Customer deleted');
                    navigate('/customers');
                  } catch (e) { toast.error('Failed to delete customer'); }
                }}
                data-testid="delete-customer-button"
              >
                Delete
              </Button>
            </div>
          )}
        </div>

        {!isNew && <UpdatesDialog open={updatesOpen} onOpenChange={setUpdatesOpen} entityType="customer" entityId={id} />}

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="customer-tabs">
          <TabsList>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            {!isNew && (
              <>
                <TabsTrigger value="assignations" data-testid="tab-assignations">Assignations</TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                <TabsTrigger value="loads" data-testid="tab-loads">Loads Assigned</TabsTrigger>
                <TabsTrigger value="lanes" data-testid="tab-lanes">Lanes</TabsTrigger>
                <TabsTrigger value="fuel-surcharge" data-testid="tab-fuel-surcharge">
                  <Fuel className="w-4 h-4 mr-1" /> Fuel Surcharge
                </TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes">Notes & Interactions</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <form onSubmit={handleSave} className="max-w-3xl space-y-6">
              
              {/* First Block - Company Information */}
              <div className="stats-card">
                <h3 className="text-lg font-semibold mb-6 text-[var(--tms-text)]">Company Information</h3>
                <div className="space-y-4">
                  {/* Row 1: Customer Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Customer Name *</Label>
                    <Input id="name" value={customer.name} onChange={(e) => setCustomer({...customer, name: e.target.value})} required data-testid="input-name" data-field="name" />
                  </div>
                  
                  {/* Row 2: Address */}
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input id="address" value={customer.address || ''} onChange={(e) => setCustomer({...customer, address: e.target.value})} placeholder="Street address" data-testid="input-address" />
                  </div>
                  
                  {/* Row 3: City, State, Zip Code */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input id="city" value={customer.city || ''} onChange={(e) => setCustomer({...customer, city: e.target.value})} data-testid="input-city" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input id="state" value={customer.state || ''} onChange={(e) => setCustomer({...customer, state: e.target.value})} placeholder="TX" maxLength={2} data-testid="input-state" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">Zip Code</Label>
                      <Input id="zip_code" value={customer.zip_code || ''} onChange={(e) => setCustomer({...customer, zip_code: e.target.value})} data-testid="input-zip" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Block - Contact Information */}
              <div className="stats-card">
                <h3 className="text-lg font-semibold mb-6 text-[var(--tms-text)]">Contact Information</h3>
                <div className="space-y-4">
                  {/* Row 1: Contact Name */}
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input id="contact_name" value={customer.contact_name || ''} onChange={(e) => setCustomer({...customer, contact_name: e.target.value})} placeholder="Primary contact person" data-testid="input-contact-name" />
                  </div>
                  
                  {/* Row 2: Phone, Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" value={customer.phone} onChange={(e) => setCustomer({...customer, phone: e.target.value})} required data-testid="input-phone" data-field="phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={customer.email} onChange={(e) => setCustomer({...customer, email: e.target.value})} required data-testid="input-email" data-field="email" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Block - Account Settings */}
              <div className="stats-card">
                <h3 className="text-lg font-semibold mb-6 text-[var(--tms-text)]">Account Settings</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="status">Account Status</Label>
                      <Select value={customer.account_status} onValueChange={(val) => setCustomer({...customer, account_status: val})}>
                        <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Credit Warning">Credit Warning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="credit_limit">Credit Limit</Label>
                      <Input id="credit_limit" type="number" value={customer.credit_limit} onChange={(e) => setCustomer({...customer, credit_limit: parseFloat(e.target.value) || 0})} data-testid="input-credit-limit" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="credit_terms">Credit Terms</Label>
                      <Input id="credit_terms" value={customer.credit_terms} onChange={(e) => setCustomer({...customer, credit_terms: e.target.value})} data-testid="input-credit-terms" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Save/Cancel Buttons */}
              <div className="flex gap-3">
                <Button type="submit" data-testid="save-customer-button">Save Customer</Button>
                <Button type="button" variant="outline" onClick={() => navigate('/customers')} data-testid="cancel-button">Cancel</Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="assignations" className="mt-6">
            <div className="stats-card max-w-3xl">
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-[var(--tms-primary)]" />
                <h3 className="text-lg font-semibold">Account Assignations</h3>
              </div>
              
              {/* Row 1: Account Manager and Commission */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="account_manager">Account Manager</Label>
                  <Select 
                    value={customer.account_manager_id || ''} 
                    onValueChange={(val) => {
                      const selectedUser = users.find(u => u.id === val);
                      setCustomer({
                        ...customer, 
                        account_manager_id: val,
                        account_manager_name: selectedUser?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger data-testid="select-account-manager">
                      <SelectValue placeholder="Select account manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_manager_commission">Commission %</Label>
                  <Input 
                    id="account_manager_commission"
                    type="number" 
                    step="0.1"
                    min="0"
                    max="100"
                    value={customer.account_manager_commission_pct || ''} 
                    onChange={(e) => setCustomer({
                      ...customer, 
                      account_manager_commission_pct: parseFloat(e.target.value) || 0
                    })}
                    placeholder="e.g. 5"
                    data-testid="input-manager-commission"
                  />
                </div>
              </div>

              {/* Operators Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-[var(--tms-text)]">Operators</h4>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setCustomer({
                      ...customer,
                      operators: [...(customer.operators || []), { user_id: '', name: '', commission_pct: 0 }]
                    })}
                    data-testid="add-operator-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Operator
                  </Button>
                </div>

                {(!customer.operators || customer.operators.length === 0) ? (
                  <p className="text-[var(--tms-text-muted)] text-center py-8 border rounded-lg bg-[var(--tms-hover)]">
                    No operators assigned yet. Click "Add Operator" to add one.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {customer.operators.map((operator, index) => (
                      <div key={operator.user_id || operator.id || `op-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end p-4 border rounded-lg bg-[var(--tms-hover)]">
                        <div className="md:col-span-5 space-y-2">
                          <Label>Operator</Label>
                          <Select 
                            value={operator.user_id || ''} 
                            onValueChange={(val) => {
                              const selectedUser = users.find(u => u.id === val);
                              const newOperators = [...customer.operators];
                              newOperators[index] = {
                                ...newOperators[index],
                                user_id: val,
                                name: selectedUser?.name || ''
                              };
                              setCustomer({ ...customer, operators: newOperators });
                            }}
                          >
                            <SelectTrigger data-testid={`select-operator-${index}`}>
                              <SelectValue placeholder="Select operator" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.map(user => (
                                <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-5 space-y-2">
                          <Label>Commission %</Label>
                          <Input 
                            type="number" 
                            step="0.1"
                            min="0"
                            max="100"
                            value={operator.commission_pct || ''} 
                            onChange={(e) => {
                              const newOperators = [...customer.operators];
                              newOperators[index] = {
                                ...newOperators[index],
                                commission_pct: parseFloat(e.target.value) || 0
                              };
                              setCustomer({ ...customer, operators: newOperators });
                            }}
                            placeholder="e.g. 2.5"
                            data-testid={`input-operator-commission-${index}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <button 
                            onClick={() => {
                              const newOperators = customer.operators.filter((_, i) => i !== index);
                              setCustomer({ ...customer, operators: newOperators });
                            }}
                            className="p-2 text-[var(--tms-error)] hover:text-red-700 hover:bg-red-50 rounded"
                            data-testid={`remove-operator-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support Operators Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-[var(--tms-text)]">Support Operators</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCustomer({ ...customer, support_operators: [...(customer.support_operators || []), { user_id: '', name: '', commission_pct: 0 }] })}
                    data-testid="add-support-operator-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Support Operator
                  </Button>
                </div>
                {(!customer.support_operators || customer.support_operators.length === 0) ? (
                  <p className="text-[var(--tms-text-muted)] text-center py-6 border rounded-lg bg-[var(--tms-hover)]">No support operators assigned yet.</p>
                ) : (
                  <div className="space-y-3">
                    {customer.support_operators.map((op, index) => (
                      <div key={op.user_id || op.id || `sop-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end p-4 border rounded-lg bg-[var(--tms-hover)]">
                        <div className="md:col-span-10 space-y-2">
                          <Label>Support Operator</Label>
                          <Select
                            value={op.user_id || ''}
                            onValueChange={(val) => {
                              const u = users.find(x => x.id === val);
                              const next = [...customer.support_operators];
                              next[index] = { ...next[index], user_id: val, name: u?.name || '' };
                              setCustomer({ ...customer, support_operators: next });
                            }}
                          >
                            <SelectTrigger data-testid={`select-support-operator-${index}`}><SelectValue placeholder="Select support operator" /></SelectTrigger>
                            <SelectContent>
                              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}{u.role ? ` (${u.role.replace('_', ' ')})` : ''}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2">
                          <button
                            onClick={() => setCustomer({ ...customer, support_operators: customer.support_operators.filter((_, i) => i !== index) })}
                            className="p-2 text-[var(--tms-error)] hover:text-red-700 hover:bg-red-50 rounded"
                            data-testid={`remove-support-operator-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-3 mt-6 pt-6 border-t">
                <Button onClick={handleSave} data-testid="save-assignations-button">Save Changes</Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <div className="stats-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Documents</h3>
                <Button size="sm" data-testid="add-document-button"><Plus className="w-4 h-4 mr-1" /> Upload Document</Button>
              </div>
              {documents.length === 0 ? (
                <p className="text-[var(--tms-text-muted)] text-center py-8">No documents yet</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded" data-testid={`doc-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[var(--tms-text-muted)]" />
                        <div>
                          <p className="font-medium">{doc.file_name}</p>
                          <p className="text-sm text-[var(--tms-text-muted)]">{doc.document_type}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="loads" className="mt-6">
            <div className="stats-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Assigned Loads <span className="text-sm font-normal text-[var(--tms-text-muted)]">({loads.length})</span></h3>
                <Button size="sm" variant="outline" onClick={() => navigate(`/loads?customer=${id}`)} data-testid="open-in-loads-page">
                  Open in Loads page
                </Button>
              </div>
              <LoadsSpreadsheet
                loads={loads}
                facilities={facilities}
                customers={customer ? [customer] : []}
                isRestrictedUser={false}
                listColumns={listColumns}
                onLoadSaved={(updatedLoad) => setLoads(prev => prev.map(l => l.id === updatedLoad.id ? updatedLoad : l))}
                onLoadDuplicated={(newLoad) => setLoads(prev => [newLoad, ...prev])}
                emptyMessage="No loads assigned to this customer"
              />
            </div>
          </TabsContent>

          <TabsContent value="lanes" className="mt-6">
            <div className="stats-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[var(--tms-primary)]" />
                  Lanes
                </h3>
              </div>
              
              {/* Add Lane Form */}
              <div className="border rounded-lg p-4 mb-6 bg-[var(--tms-hover)]">
                <h4 className="font-medium mb-3">Add New Lane</h4>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label>Origin *</Label>
                    <Input 
                      placeholder="e.g. Dallas, TX" 
                      value={newLane.origin} 
                      onChange={(e) => setNewLane({...newLane, origin: e.target.value})}
                      data-testid="input-lane-origin"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Destination *</Label>
                    <Input 
                      placeholder="e.g. Chicago, IL" 
                      value={newLane.destination} 
                      onChange={(e) => setNewLane({...newLane, destination: e.target.value})}
                      data-testid="input-lane-destination"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Stops</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={newLane.stops} 
                      onChange={(e) => setNewLane({...newLane, stops: parseInt(e.target.value) || 0})}
                      data-testid="input-lane-stops"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Line Haul ($)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={newLane.line_haul} 
                      onChange={(e) => setNewLane({...newLane, line_haul: parseFloat(e.target.value) || 0})}
                      data-testid="input-lane-linehaul"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Accessorials ($)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={newLane.accessorials} 
                      onChange={(e) => setNewLane({...newLane, accessorials: parseFloat(e.target.value) || 0})}
                      data-testid="input-lane-accessorials"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button size="sm" onClick={handleAddLane} data-testid="add-lane-button">
                    <Plus className="w-4 h-4 mr-1" /> Add Lane
                  </Button>
                </div>
              </div>

              {/* Lanes Table */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Origin</th>
                    <th>Destination</th>
                    <th>Stops</th>
                    <th>Line Haul</th>
                    <th>Accessorials</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lanes.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8 text-[var(--tms-text-muted)]">
                        No lanes defined for this customer yet
                      </td>
                    </tr>
                  ) : (
                    lanes.map((lane) => (
                      <tr key={lane.id} data-testid={`lane-${lane.id}`}>
                        <td className="font-medium">{lane.origin}</td>
                        <td className="font-medium">{lane.destination}</td>
                        <td className="text-center">{lane.stops}</td>
                        <td className="font-mono">${(lane.line_haul || 0).toLocaleString()}</td>
                        <td className="font-mono">${(lane.accessorials || 0).toLocaleString()}</td>
                        <td className="font-mono font-bold text-[var(--tms-success)]">
                          ${((lane.line_haul || 0) + (lane.accessorials || 0)).toLocaleString()}
                        </td>
                        <td>
                          <button 
                            onClick={() => handleDeleteLane(lane.id)}
                            className="text-[var(--tms-error)] hover:text-red-700"
                            data-testid={`delete-lane-${lane.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="fuel-surcharge" className="mt-6">
            <CustomerFuelSurchargeTab customerId={id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-6">
            <div className="stats-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Notes & Interactions</h3>
                <Button size="sm" onClick={handleAddNote} data-testid="add-note-button"><Plus className="w-4 h-4 mr-1" /> Add Note</Button>
              </div>
              {interactions.length === 0 ? (
                <p className="text-[var(--tms-text-muted)] text-center py-8">No interactions yet</p>
              ) : (
                <div className="space-y-3">
                  {interactions.map((inter) => (
                    <div key={inter.id} className="border rounded p-4" data-testid={`interaction-${inter.id}`}>
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-[var(--tms-text-muted)] mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-semibold">{inter.subject}</p>
                            <span className="text-xs text-[var(--tms-text-muted)]">{new Date(inter.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-[var(--tms-text-secondary)]">{inter.content}</p>
                          <p className="text-xs text-[var(--tms-text-muted)] mt-2">By: {inter.created_by_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default CustomerDetail;